import LiveKitConnection from './LiveKitConnection';
import { ParticipantState } from './signalSchema';

// Receives other participants' streams over a single LiveKit connection per page, and hands out per-stream
// handles with the same interface as OmeReceiver. A stream is a participant whose identity is the stream id.
export default class LiveKitRoomReceiver {
	// getToken: async () => subscribe-only token with an identity of its own.
	constructor(url, getToken) {
		this._url = url;
		this._getToken = getToken;
		this._connection = null;
		this._connecting = null;
		this._participants = new Map(); // sid -> ParticipantInfo
		this._streams = new Map(); // identity -> { handlers, mediaStream, subscribed: Set<trackSid> }
	}

	// Handle on one stream: start() / stop(), onStream(MediaStream) and onClose() like OmeReceiver's.
	openStream(identity, handlers) {
		return {
			start: () => this._addStream(identity, handlers),
			stop: () => this._removeStream(identity, handlers),
		};
	}

	close() {
		const connection = this._connection;
		this._connection = null;
		this._connecting = null;
		connection?.close();
	}

	async _addStream(identity, handlers) {
		this._streams.set(identity, { handlers, mediaStream: null, subscribed: new Set() });
		try {
			await this._ensureConnected();
			this._subscribeAll();
		} catch(e) {
			console.error('LiveKit: could not connect to receive', e);
			if(this._streams.get(identity)?.handlers === handlers) {
				this._streams.delete(identity);
				handlers.onClose?.();
			}
		}
	}

	_removeStream(identity, handlers) {
		const entry = this._streams.get(identity);
		if(!entry || entry.handlers !== handlers) return;
		this._streams.delete(identity);
		const participant = this._findParticipant(identity);
		if(participant && entry.subscribed.size) this._connection?.subscribe(participant.sid, [ ...entry.subscribed ], false);
		if(!this._streams.size) this.close();
	}

	_ensureConnected() {
		if(this._connection) return this._connecting;
		const connection = new LiveKitConnection(this._url, {
			onParticipants: participants => this._updateParticipants(participants),
			onTrack: e => this._handleTrack(e),
			onClose: () => this._handleClose(connection),
		});
		this._connection = connection;
		this._participants.clear();
		this._connecting = this._getToken()
			.then(token => connection.connect(token))
			.catch(e => {
				connection.close();
				throw e;
			});
		return this._connecting;
	}

	_handleClose(connection) {
		if(this._connection !== connection) return;
		this._connection = null;
		this._connecting = null;
		// Every stream's owner reconnects on its own, like after an OmeReceiver drops.
		const streams = [ ...this._streams.values() ];
		this._streams.clear();
		for(const { handlers } of streams) handlers.onClose?.();
	}

	_findParticipant(identity) {
		for(const p of this._participants.values()) if(p.identity === identity) return p;
		return null;
	}

	_updateParticipants(participants) {
		for(const p of participants) {
			if(p.state === ParticipantState.DISCONNECTED) {
				this._participants.delete(p.sid);
				const entry = this._streams.get(p.identity);
				if(entry) {
					this._streams.delete(p.identity);
					entry.handlers.onClose?.();
				}
			} else {
				this._participants.set(p.sid, p);
			}
		}
		this._subscribeAll();
	}

	// Subscribes to the tracks of every stream someone's watching that aren't subscribed yet.
	_subscribeAll() {
		if(!this._connection?.participant) return;
		for(const [ identity, entry ] of this._streams) {
			const participant = this._findParticipant(identity);
			if(!participant) continue;
			const trackSids = (participant.tracks || []).map(t => t.sid).filter(sid => !entry.subscribed.has(sid));
			if(!trackSids.length) continue;
			for(const sid of trackSids) entry.subscribed.add(sid);
			this._connection.subscribe(participant.sid, trackSids);
		}
	}

	// LiveKit names each subscribed stream "<participant sid>|<track sid>".
	_handleTrack(e) {
		const participantSid = (e.streams[0]?.id || '').split('|')[0];
		const participant = this._participants.get(participantSid);
		const entry = participant && this._streams.get(participant.identity);
		if(!entry) return;
		if(!entry.mediaStream) entry.mediaStream = new MediaStream();
		for(const old of entry.mediaStream.getTracks().filter(t => t.kind === e.track.kind)) entry.mediaStream.removeTrack(old);
		entry.mediaStream.addTrack(e.track);
		entry.handlers.onStream?.(entry.mediaStream);
	}
}
